import { StorefrontConfig } from "src/storefront/types/storefront";
import { AlertContext } from "src/components/alert";
import { Cart, CartItem } from "src/storefront/types/cart";
import { CartItemEvent, CartItemEventSerializer } from "src/analytics/cart_item_event_serializer";
import { Catalog } from "src/storefront/types/catalog";
import { CartWithAlertMessage } from "src/storefront/decoders";

const buildUpdateCart = (
  cart: Cart | null,
  catalog: Catalog | undefined,
  config: StorefrontConfig,
  setCartItems: (cartItems: CartItem[]) => Promise<CartWithAlertMessage>,
  showAlert: AlertContext,
  pushDataEvent: (cartItemEvent: CartItemEvent) => void,
) => {
  return async (updatedCartItem: CartItem) => {
    if (!cart) throw "Can't update a cart without an existing cart";

    const previousCartItem = cart.cartItems.find(ci => ci.idTarget == updatedCartItem.idTarget);

    const itemToMergeInto = [...cart.cartItems].reverse().find(ci => {
      return (ci.idTarget != updatedCartItem.idTarget && ci.isStackableWith(updatedCartItem));
    });

    const newCartItems = [...cart.cartItems].reduce<CartItem[]>((accl, ci) => {
      if (itemToMergeInto && ci.idTarget == itemToMergeInto.idTarget) {
        accl.push(itemToMergeInto.copyWith({ quantity: itemToMergeInto.quantity + updatedCartItem.quantity }));
      } else if (!itemToMergeInto && ci.idTarget == updatedCartItem.idTarget) {
        accl.push(updatedCartItem);
      } else if (ci.idTarget != updatedCartItem.idTarget && ci.idTarget != itemToMergeInto?.idTarget) {
        accl.push(ci);
      }

      return accl;
    }, []);

    const mutationResult = await setCartItems(newCartItems);

    if (mutationResult.alertMessage) {
      showAlert(mutationResult.alertMessage);
    }

    if (catalog && previousCartItem) {
      const serializer = new CartItemEventSerializer(catalog, config.defaultItemsEventAttributes)

      if (updatedCartItem.quantity > previousCartItem.quantity) {
        const addToCartEvent = serializer.addToCartEvent(
          updatedCartItem.copyWith({ quantity: updatedCartItem.quantity - previousCartItem.quantity })
        );

        pushDataEvent(addToCartEvent);
      } else if (updatedCartItem.quantity < previousCartItem.quantity) {
        const removeFromCartEvent = serializer.removeFromCartEvent(
          updatedCartItem.copyWith({ quantity: previousCartItem.quantity - updatedCartItem.quantity })
        );

        pushDataEvent(removeFromCartEvent);
      }
    }

    return mutationResult;
  }
}

export default buildUpdateCart;
